/* Tabla consolidada de ventas: formato, exportación a Excel y PDF.
   Las cifras salen siempre de la respuesta del servidor (ya filtrada por sucursales, grupos, eventos y fechas); aquí solo se
   presentan. Los importes (type "currency") y los conteos (type "count") son valores distintos: nunca se suman entre sí. */

export const CONSOLIDATED_METRIC_IDS = ['ventas_consolidadas', 'consolidated_sales'];
export const isConsolidatedMetric = (metricId) => CONSOLIDATED_METRIC_IDS.includes(metricId);

const nf = (min, max) => new Intl.NumberFormat('es-PA', { minimumFractionDigits: min, maximumFractionDigits: max });
const moneyFormat = nf(2, 2);
const countFormat = nf(0, 0);

export const formatMoney = (value) => {
  const number = Number(value || 0);
  return `${number < 0 ? '-' : ''}$${moneyFormat.format(Math.abs(number))}`;
};

export const formatCell = (column, value) => {
  if (value === undefined || value === null || value === '') return column.type === 'text' ? '' : formatCell({ type: column.type }, 0);
  if (column.type === 'currency') return formatMoney(value);
  if (column.type === 'count') return countFormat.format(Number(value));
  return String(value);
};

export const safeFileName = (value) => String(value || 'reporte')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '').toLowerCase() || 'reporte';

/* Petición del Excel. El servidor vuelve a resolver la tabla con los mismos filtros y permisos que la pantalla
   (no se envían las filas: el archivo no puede tener más ni distintos datos que lo que el servidor autoriza). */
export const buildExcelExportRequest = ({ businessCode, slot, layoutId, slotIndex, scopeRequest, eventIds, metricFilters }) => ({
  business_code: businessCode,
  report_key: 'overview_ventas_consolidadas',
  filters: metricFilters,
  branchoffice_code: scopeRequest.branchoffice_code,
  group_ids: scopeRequest.group_ids,
  event_ids: eventIds,
  blocks: [{
    metricId: slot.metricId,
    chartType: slot.chartType,
    config: slot.config || {},
    layout_id: layoutId,
    slot_index: slotIndex,
    widgetId: `${layoutId}:${slotIndex}`,
  }],
});

/* Líneas de contexto del PDF, escritas con lo que el servidor aplicó (scope) y el rango de fechas pedido */
export const buildScopeLines = ({ scope, metricFilters, branchLabel }) => {
  const lines = [];
  const range = metricFilters && metricFilters.date_range === 'custom'
    ? `${metricFilters.date_from} al ${metricFilters.date_to}`
    : ({ today: 'Hoy', week: 'Semana', month: 'Mes', quarter: 'Trimestre', year: 'Año' }[metricFilters && metricFilters.date_range] || '');
  if (range) lines.push(['Período', range]);
  if (branchLabel) lines.push(['Sucursales', branchLabel]);
  if (scope && scope.branches && scope.branches.groups.length > 0) lines.push(['Grupos', scope.branches.groups.map((group) => group.name).join(', ')]);
  if (scope && scope.events && scope.events.applied.length > 0) {
    lines.push(['Eventos', scope.events.applied.map((event) => event.name).join(', ')]);
    if (scope.unattributed_refunds && scope.unattributed_refunds.count > 0) {
      lines.push(['Aviso', `${scope.unattributed_refunds.count} devoluciones sin evento (${formatMoney(scope.unattributed_refunds.total)}) no incluidas`]);
    }
  }
  return lines;
};

/* Definición del PDF (pdfMake): horizontal, encabezado, contexto, tabla con la fila de totales en negrita */
export const buildConsolidatedPdf = ({ entry, title = 'Ventas consolidadas', scopeLines = [], generatedAt = new Date() }) => {
  const columns = entry.columns || [];
  const alignOf = (column) => (column.type === 'text' ? 'left' : 'right');

  const header = columns.map((column) => ({ text: column.label, style: 'tableHeader', alignment: alignOf(column) }));
  const body = (entry.data || []).map((row) => columns.map((column) => ({ text: formatCell(column, row[column.id]), alignment: alignOf(column) })));
  const totals = entry.totals
    ? [columns.map((column) => ({ text: formatCell(column, entry.totals[column.id]), alignment: alignOf(column), bold: true, fillColor: '#F3F4F6' }))]
    : [];

  return {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [28, 28, 28, 28],
    content: [
      { text: title, style: 'title' },
      ...scopeLines.map(([label, value]) => ({ text: [{ text: `${label}: `, bold: true }, value], margin: [0, 0, 0, 2], fontSize: 10 })),
      { text: `Generado: ${generatedAt.toLocaleString('es-PA')}`, fontSize: 9, color: '#6b7280', margin: [0, 4, 0, 10] },
      {
        table: { headerRows: 1, widths: columns.map((column, index) => (index === 0 ? '*' : 'auto')), body: [header, ...body, ...totals] },
        layout: 'lightHorizontalLines',
        fontSize: 9,
      },
    ],
    styles: {
      title: { fontSize: 16, bold: true, margin: [0, 0, 0, 8] },
      tableHeader: { bold: true, fillColor: '#111827', color: '#ffffff' },
    },
  };
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const filenameFromDisposition = (header) => {
  const match = /filename="?([^";]+)"?/i.exec(header || '');
  return match ? match[1] : null;
};
